import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BallGlbFiles } from './ballGlbFiles';
import { Ball } from './ball';
import { degreesToRadians, getNumberAngle, getRandomItem } from '../utils';
import { Wheel } from './wheel';
import { cameraPos2, DIRECTIONS, SCENE_OBJ_NAMES } from '../constants';
import { cameraPositionAnimation } from '../camera';
import { sceneObjects } from '../index';
import { threeJsSounds } from '../threeJsSounds';

export class AnimationService {
  direction = DIRECTIONS.RIGHT;

  currentBall = null;

  wheel = new Wheel();

  isBallFollowerOnWheel = false;

  resultNumber = 0;

  isBallInCollision = false;

  constructor() {
    this.loader = new GLTFLoader();
    this.ballAnimations = { [DIRECTIONS.RIGHT]: [], [DIRECTIONS.LEFT]: [] };

    this.loadBallAnimations();
    this.ballFollowerOnWheel();
    this.checkCollisionWheelAndBall();
  }

  setBallAnimationCallBack(f) {
    this.ballAnimationCallBack = f;
  }

  ballAnimationCallBack() {}

  setIsBallInCollision(bool) {
    this.isBallInCollision = bool;
  }

  async checkCollisionWheelAndBall() {
    requestAnimationFrame(() => this.checkCollisionWheelAndBall());
    if (!this.currentBall) {
      return;
    }
    const ball = this.currentBall.ballMesh;
    const wheel = this.wheel.getWheelCollisionMesh();

    if (!ball || !wheel) {
      // console.error('Ball or Wheel mesh is not available.');
      return;
    }

    const wheelBoundingBox = new THREE.Box3().setFromObject(wheel);
    const ballBoundingSphere = new THREE.Sphere();
    ballBoundingSphere.copy(ball.geometry.boundingSphere).applyMatrix4(ball.matrixWorld);
    const collisionDetected = wheelBoundingBox.intersectsSphere(ballBoundingSphere);

    if (collisionDetected) {
      if (!this.isBallInCollision) {
        threeJsSounds.randomHit();
        await threeJsSounds.stopBallLoop();
        setTimeout(async () => {
          await threeJsSounds.randomHit();
        }, 200);
      }
      this.setIsBallInCollision(true);
    } else {
      this.setIsBallInCollision(false);
    }
  }

  setResultNumber(num) {
    this.resultNumber = num;
  }

  setIsBallFollowerOnWheel(bool) {
    this.isBallFollowerOnWheel = bool;
  }

  setDirection(direction) {
    this.direction = direction;
  }

  setCurrentBall(obj) {
    this.currentBall?.removeFromScene();
    this.currentBall = obj;
    this.currentBall.addScene();
  }

  startBallHit(time = 900) {
    threeJsSounds.ballHit();
    setTimeout(() => {
      if (!this.isBallFollowerOnWheel) {
        this.startBallHit(time - 250);
      }
    }, time);
  }

  spin({ num, direction = DIRECTIONS.LEFT }) {
    threeJsSounds.ballLoop();
    const camera = sceneObjects.get(SCENE_OBJ_NAMES.camera);
    cameraPositionAnimation(camera, cameraPos2);
    this.setIsBallFollowerOnWheel(false);
    this.setResultNumber(num);
    this.setDirection(direction);
    this.setCurrentBall(this.getRandomAnimationByDirection());
    this.currentBall.playAnimation();
    this.wheel.startRotationForSpin({
      num: num,
      duration: this.currentBall.animationDuration,
      direction: this.direction,
      deviation: this.currentBall.deviation,
    });
  }

  getRandomRightAnimation() {
    return getRandomItem(this.ballAnimations.right);
  }

  getRandomLeftAnimation() {
    return getRandomItem(this.ballAnimations.left);
  }

  getRandomAnimationByDirection() {
    if (this.direction === DIRECTIONS.RIGHT) {
      const randomRightAnimation = this.getRandomRightAnimation();
      const index = this.ballAnimations.right.indexOf(randomRightAnimation);
      return randomRightAnimation;
    }

    const randomLeftAnimation = this.getRandomLeftAnimation();
    const index = this.ballAnimations.left.indexOf(randomLeftAnimation);
    return randomLeftAnimation;
  }

  loadBallAnimations() {
    BallGlbFiles.right.forEach((ballFile, index) => {
      this.loader.load(
        ballFile,
        (gltf) => {
          this.ballAnimations[DIRECTIONS.RIGHT][index] = new Ball(gltf);
        },
        undefined,
        () => {}
      );
    });

    BallGlbFiles.left.forEach((ballFile, index) => {
      this.loader.load(
        ballFile,
        (gltf) => {
          this.ballAnimations[DIRECTIONS.LEFT][index] = new Ball(gltf);
        },
        undefined,
        () => {}
      );
    });
  }

  ballFollowerOnWheel() {
    requestAnimationFrame(() => this.ballFollowerOnWheel());
    if (!this.isBallFollowerOnWheel || !this.currentBall) {
      return;
    }

    const wheelMesh = this.wheel.getWheelMesh();
    if (!wheelMesh || !wheelMesh?.rotation) {
      return;
    }

    this.currentBall.model.rotation.y =
      wheelMesh.rotation.y +
      this.currentBall.deviation +
      degreesToRadians(getNumberAngle(this.resultNumber)) +
      Math.PI / 2;
  }

  clearAnimations() {
    this.setIsBallFollowerOnWheel(false);
    this.wheel.clearAnimations();
    this.currentBall?.stopAnimation();
    this.currentBall?.removeFromScene();
    this.currentBall = null;
  }
}

export const animationService = new AnimationService();

// document.addEventListener('keydown', (e) => {
//   if (e.key === ' ') {
//     const num = getRandomNumberInRange(0, 35);
//     animationService.spin({ num });
//   }
// });

// setTimeout(() => {
//   setDevMoveForThreeJsCanvas();
// }, 500);
